import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Sparkles, Loader2, Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface GeneratedTag {
  name: string;
  category: 'lab' | 'symptom' | 'condition' | 'medication' | 'vital_sign';
  color: string;
  reasoning: string;
}

interface GeneratedTagsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tags: GeneratedTag[];
  healthArea: string;
  onAddTags: (selectedTags: GeneratedTag[]) => Promise<void>;
  onRegenerate: () => Promise<void>;
  isRegenerating: boolean;
}

const CATEGORY_LABELS: Record<GeneratedTag['category'], string> = {
  lab: 'Exame',
  symptom: 'Sintoma',
  condition: 'Condição',
  medication: 'Medicação',
  vital_sign: 'Sinal Vital'
};

export function GeneratedTagsDialog({
  open,
  onOpenChange,
  tags,
  healthArea,
  onAddTags,
  onRegenerate,
  isRegenerating
}: GeneratedTagsDialogProps) {
  const [selectedTags, setSelectedTags] = useState<Set<string>>(
    new Set(tags.map(t => t.name))
  );
  const [isAdding, setIsAdding] = useState(false);

  const toggleTag = (tagName: string) => {
    const newSelected = new Set(selectedTags);
    if (newSelected.has(tagName)) {
      newSelected.delete(tagName);
    } else {
      newSelected.add(tagName);
    }
    setSelectedTags(newSelected);
  };

  const handleAddTags = async () => {
    setIsAdding(true);
    try {
      const tagsToAdd = tags.filter(t => selectedTags.has(t.name));
      await onAddTags(tagsToAdd);
      onOpenChange(false);
    } finally {
      setIsAdding(false);
    }
  };

  const handleRegenerate = async () => {
    await onRegenerate();
    // Reset selection with new tags
    setSelectedTags(new Set(tags.map(t => t.name)));
  };

  const selectedCount = selectedTags.size;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            Tags Sugeridas para {healthArea}
          </DialogTitle>
          <DialogDescription>
            Selecione as tags que deseja adicionar ao seu sistema de monitoramento.
            Todas estão selecionadas por padrão.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 py-4">
          {tags.map((tag) => {
            const isSelected = selectedTags.has(tag.name);
            
            return (
              <div
                key={tag.name}
                className={`
                  flex items-start gap-3 p-3 rounded-lg border-2 transition-all cursor-pointer
                  ${isSelected 
                    ? 'border-primary bg-primary/5' 
                    : 'border-border hover:border-muted-foreground/30'
                  }
                `}
                onClick={() => toggleTag(tag.name)}
              >
                <Checkbox
                  checked={isSelected}
                  onCheckedChange={() => toggleTag(tag.name)}
                  className="mt-1"
                />
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge 
                      style={{ backgroundColor: tag.color }}
                      className="text-white shrink-0"
                    >
                      {tag.name}
                    </Badge>
                    
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="w-4 h-4 text-muted-foreground shrink-0" />
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-xs">
                          <p className="text-sm">{tag.reasoning}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  
                  <p className="text-xs text-muted-foreground">
                    {CATEGORY_LABELS[tag.category]}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={handleRegenerate}
            disabled={isRegenerating || isAdding}
          >
            {isRegenerating ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Gerando...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-2" />
                Gerar Novamente
              </>
            )}
          </Button>

          <Button
            onClick={handleAddTags}
            disabled={selectedCount === 0 || isAdding || isRegenerating}
          >
            {isAdding ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Adicionando...
              </>
            ) : (
              `Adicionar ${selectedCount} ${selectedCount === 1 ? 'tag' : 'tags'}`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
